import { FC } from 'react'
import styles from 'styles/components/modal.module.scss'

interface DiffItem {
  type: 'new' | 'update' | 'skip'
  row: {
    wholesalerName: string
    productCode: string
    productName: string
    materialName: string
    costPrice: string
  }
  usedCosting: boolean
  changes?: {
    field: string
    oldValue: string
    newValue: string
  }[]
}

interface DiffSummary {
  newCount: number
  updateCount: number
  skipCount: number
  items: DiffItem[]
}

interface Props {
  isOpen: boolean
  diffSummary: DiffSummary
  onConfirm: () => void
  onCancel: () => void
}

const BulkUploadConfirmModal: FC<Props> = ({
  isOpen,
  diffSummary,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null

  return (
    <div className={styles.modalOverlay} onClick={onCancel}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div
          className={styles.modalHeader}
          style={{ backgroundColor: 'rgba(226, 232, 240, 0.5)' }}
        >
          <h2>登録確認</h2>
        </div>

        <div className={styles.modalBody} style={{ backgroundColor: '#fff' }}>
          <div style={{ marginBottom: '24px' }}>
            <p
              style={{
                marginBottom: '16px',
                fontSize: '14px',
                color: '#4A5568',
              }}
            >
              以下の内容で一括登録を行います。
            </p>

            <div
              style={{
                padding: '16px',
                backgroundColor: '#FFF9E6',
                border: '1px solid #F4A460',
                borderRadius: '4px',
                marginBottom: '16px',
              }}
            >
              <p
                style={{
                  fontSize: '14px',
                  marginBottom: '8px',
                  color: '#4A5568',
                }}
              >
                ✓ 登録後、すべて原価計算に使用されます
              </p>
              <p style={{ fontSize: '14px', color: '#4A5568' }}>
                ✓ 一度登録すると取り消せません
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h3
                style={{
                  fontSize: '16px',
                  marginBottom: '8px',
                  color: '#4A5568',
                  fontWeight: 'bold',
                }}
              >
                【登録内容】
              </h3>
              <p style={{ fontSize: '14px', color: '#4A5568' }}>
                ・新規登録: {diffSummary.newCount}件
              </p>
              <p style={{ fontSize: '14px', color: '#4A5568' }}>
                ・更新: {diffSummary.updateCount}件
              </p>
              {diffSummary.skipCount > 0 && (
                <p style={{ fontSize: '14px', color: '#8B8B8B' }}>
                  ・変更なし（スキップ）: {diffSummary.skipCount}件
                </p>
              )}
            </div>

            {diffSummary.items.filter((item) => item.type !== 'skip').length >
              0 && (
              <div>
                <h3
                  style={{
                    fontSize: '16px',
                    marginBottom: '12px',
                    borderBottom: '2px solid #E2E8F0',
                    paddingBottom: '8px',
                    color: '#4A5568',
                    fontWeight: 'bold',
                  }}
                >
                  【変更詳細】
                </h3>
                <div
                  style={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    border: '1px solid #E2E8F0',
                    borderRadius: '4px',
                    padding: '12px',
                    backgroundColor: 'rgba(226, 232, 240, 0.5)',
                  }}
                >
                  {diffSummary.newCount > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <h4
                        style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          marginBottom: '8px',
                          color: '#279a40',
                        }}
                      >
                        ▼ 新規登録（{diffSummary.newCount}件）
                      </h4>
                      {diffSummary.items
                        .filter((item) => item.type === 'new')
                        .map((item, index) => (
                          <div
                            key={`new-${index}`}
                            style={{
                              fontSize: '13px',
                              padding: '8px 12px',
                              backgroundColor: '#fff',
                              marginBottom: '4px',
                              borderRadius: '4px',
                              border: '1px solid #E2E8F0',
                              color: '#4A5568',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <div>
                              {index + 1}. {item.row.materialName} /{' '}
                              {item.row.wholesalerName} / 商品コード:{' '}
                              {item.row.productCode} / 単価: ¥
                              {item.row.costPrice}
                            </div>
                            {item.usedCosting && (
                              <div
                                style={{
                                  backgroundColor: '#279a40',
                                  color: '#fff',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  whiteSpace: 'nowrap',
                                  marginLeft: '8px',
                                }}
                              >
                                ✓ 原価計算に使用
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  )}

                  {diffSummary.updateCount > 0 && (
                    <div>
                      <h4
                        style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          marginBottom: '8px',
                          color: '#F4A460',
                        }}
                      >
                        ▼ 更新（{diffSummary.updateCount}件）
                      </h4>
                      {diffSummary.items
                        .filter((item) => item.type === 'update')
                        .map((item, index) => (
                          <div
                            key={`update-${index}`}
                            style={{
                              fontSize: '13px',
                              padding: '12px',
                              backgroundColor: '#fff',
                              marginBottom: '12px',
                              borderRadius: '4px',
                              border: '1px solid #E2E8F0',
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 'bold',
                                marginBottom: '8px',
                                color: '#4A5568',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <div>
                                {index + 1}. {item.row.materialName} /{' '}
                                {item.row.wholesalerName} / 商品コード:{' '}
                                {item.row.productCode} / 単価: ¥
                                {item.row.costPrice}
                              </div>
                              {item.usedCosting && (
                                <div
                                  style={{
                                    backgroundColor: '#279a40',
                                    color: '#fff',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                    marginLeft: '8px',
                                  }}
                                >
                                  ✓ 原価計算に使用
                                </div>
                              )}
                            </div>
                            {item.changes && item.changes.length > 0 && (
                              <div style={{ paddingLeft: '16px' }}>
                                {item.changes.map((change, changeIndex) => (
                                  <div
                                    key={changeIndex}
                                    style={{
                                      fontSize: '12px',
                                      marginBottom: '4px',
                                      color: '#8B8B8B',
                                    }}
                                  >
                                    - {change.field}: {change.oldValue} →{' '}
                                    {change.newValue}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            onClick={onCancel}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              backgroundColor: '#fff',
              border: '1px solid #E2E8F0',
              borderRadius: '4px',
              cursor: 'pointer',
              marginRight: '12px',
              color: '#4A5568',
              fontWeight: '500',
            }}
          >
            キャンセル
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              backgroundColor: '#F4A460',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            登録する
          </button>
        </div>
      </div>
    </div>
  )
}

export default BulkUploadConfirmModal
